import { Colors } from '@tableau/icons/util/Colors';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { classes, cssClass, fillStyle, flexColumnStyle } from '../../../style';
import { SpaceContainerStyles } from '../../style/SpaceContainerStyles';

const cssPrefix = 'ContentExplorer';

export namespace ExplorerStyles {
  export const TOOLBAR_HEIGHT = 42;
  export const LIST_VIEW_ROW_OUTER_HEIGHT = 44;
  export const TRANSITION_TIME = .3;
  export const EMPTY_STATE_ICON_SIZE = 80;

  export const ExplorerContainer = cssClass(cssPrefix, 'container', {
    ...flexColumnStyle,
    ...fillStyle
  });

  export const Toolbar = cssClass(cssPrefix, 'toolbar', {
    fontWeight: 'normal',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    marginRight: SpaceContainerStyles.GlobalSpaceHorizontalPadding,
    color: CommonStyles.Colors.Grey51
  });

  export const ToolbarButtonGroup = cssClass(cssPrefix, 'toolbar-button-group', {
    height: TOOLBAR_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    $nest: {
      '&>*': {
          margin: '0 6px',
        },
        '&>*:last-child' : {
          marginRight: 0
        },
        '&>*:first-child' : {
          marginLeft: 0
      }
    }
  });

  const dividerHeight = 20;
  export const ToolbarButtonDivider = cssClass(cssPrefix, 'toolbar-button-divider', {
    width: 1,
    height: dividerHeight,
    backgroundColor: TabStyles.Colors.L3
  });

  export const disabledCellStyle: React.CSSProperties ={
    opacity: TabStyles.Opacity.Disabled,
    pointerEvents: 'none'
  };

  export const ToolbarButtonWidthPadding: React.CSSProperties = {
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.MediumSpace,
    minWidth: 0 // 0 is the default
  };

  export const ToolbarDropdownLabel = cssClass(cssPrefix, 'toolbar-dropdown-label', {
    marginRight: '0px',
  });

  export const ToolbarSingleSelectDropdownLabel = cssClass(cssPrefix, 'toolbar-single-select-label', {
    marginRight: '0px',
  });

  export const ToolbarDropdownLabelDisabled = cssClass(cssPrefix, 'toolbar-dropdown-label-disabled', {
    marginRight: '0px',
    color: TabStyles.Typography.Color.Disabled
  });

  export function toolbarDropdownLabelClass(disabled: boolean) {
    return disabled ? ToolbarDropdownLabelDisabled : ToolbarDropdownLabel;
  }

  export const DefaultAdvancedFilterPanelWidth = 242;
  export const MaxAdvancedFilterPanelWidth = 340;

  export const ExplorerMainArea = cssClass(cssPrefix, 'main-area', {
    flex: 1,

    // Even though the actual card view scroll container is a few levels deeper than this,
    // adding "overflow: auto" here somehow makes the card view scrolling container work properly
    // on Firefox/Edge (TFS 776617).
    // https://stackoverflow.com/questions/49662361/firefox-flex-how-to-prevent-flex-items-from-growing-too-tall-with-flex-directi
    overflowY: 'auto',

    // We animate the filter panel so it appears to slide in and out by moving offscreen to the right
    // via absolute positioning, rather than decreasing its width to zero (which would have the
    // appearance of squishing its contents during the animation). So we must keep the filter panel
    // hidden when it's offscreen to the right.
    overflowX: 'hidden',

    // Allow stuff inside to be absolutely positioned
    position: 'relative',
  });

  export const ExplorerMainAreaForCardView = cssClass(cssPrefix, 'main-area-for-card-view', {
    borderTop: `1px solid ${CommonStyles.Colors.DividerLineColor}`,
  });

  export const ExplorerContentArea = cssClass(cssPrefix, 'content-area', {
    height: '100%',
    position: 'relative',
    transition: `margin-right ${TRANSITION_TIME}s ease-in-out`,
  });

  export const AdvancedFilterPanel = cssClass(cssPrefix, 'advanced-filter-panel', {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 'auto',
    // width: is handled as state in Explorer, to make the filter panel resizable
    transition: `right ${TRANSITION_TIME}s ease-in-out`,
    overflowY: 'auto',
    borderLeft: `1px solid ${CommonStyles.Colors.DividerLineColor}`,
  });

  export const AdvancedFilterPanelHidden = cssClass(cssPrefix, 'advanced-filter-panel-hidden', {
    // right: is handled as state in Explorer, to make the filter panel resizable
    // With visibility set to hidden we ensure user cannot tab-focus-navigate into the filter panel
    visibility: 'hidden',
    // only when hiding the panel, we want to transition it to hidden instantly as soon as transition is done.
    transition: `right ${TRANSITION_TIME}s ease-in-out, visibility ${TRANSITION_TIME}s steps(1, end)`,
  });

  export function getAdvancedFilterPanelClassNames(showFilterPanel: boolean) {
    return classes(AdvancedFilterPanel, { [AdvancedFilterPanelHidden]: !showFilterPanel });
  }

  export const CreateContentMenuItemsStyleName = cssClass(cssPrefix, 'create-content-menu-items',{
    $nest: {
      '& [role^="menuitem"] > :not(.hasdetail)': {
        fontSize: TabStyles!.Typography!.Sizing!.Large,
        height: CommonStyles.Sizes.DropdownItemHeight,
        lineHeight: `${CommonStyles.Sizes.DropdownItemHeight}px`,
      },
      '& span + span': {
        marginLeft: TabStyles!.Spacing!.DefaultSpace,
      },
    },
  });

  export const buttonIconSize = 18;

  export const ExplorerViewModeMainButtonImg = cssClass(cssPrefix, 'view-mode-main-button-img', {
    height: buttonIconSize,
    width: buttonIconSize,
    verticalAlign: 'middle'
  });

  export const ExplorerViewModeButtonImg = cssClass(cssPrefix, 'view-mode-button-img', {
    height: buttonIconSize,
    width: buttonIconSize,
    marginLeft: TabStyles.Spacing.DefaultSpace
  });

  export const CheckboxCellContainer = cssClass(cssPrefix, 'checkbox-cell-container', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  });

  export const CheckboxCellContainerDisabled = cssClass(cssPrefix, 'checkbox-cell-container', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    opacity: TabStyles.Opacity.Disabled,
    pointerEvents: 'none',
  });

  export const ContentTypeCellContainer = cssClass(cssPrefix, 'content-type-cell-container', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: LIST_VIEW_ROW_OUTER_HEIGHT,
    $nest: {
      '&>div': {
        alignItems: 'center',
        // Need to override the style set directly
        // on div parent used by CertificationWatermark.CertificationTooltipComponent
        // to ensure that it is centered
        // Using 'any' here because I couldn't figure out another way to apply !important
        display: 'flex !important' as any,
        height: LIST_VIEW_ROW_OUTER_HEIGHT,
        justifyContent: 'center'
      }
    }
  });

  export const ActionsOnlyCellContainer = cssClass(cssPrefix, 'actions-only-cell-container', {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  });

  export const connectionIconStyle  = { height: '17px', paddingRight: TabStyles.Spacing.DefaultSpace };
  export const connectsToListViewIconStyle = { ...connectionIconStyle, flexShrink: 0, paddingBottom: TabStyles.Spacing.SmallSpace };
  export const statusIconStyle = { ...connectionIconStyle, marginBottom: '-3px' };

  export const contentTypeIconSize = 18;
  export const ContentTypeCellImg = cssClass(cssPrefix, 'content-type-cell-img', {
    lineHeight: 'normal',
    height: contentTypeIconSize,
    width: contentTypeIconSize,
    color: Colors.Default,
  });

  export const ListViewWaitingDiv = cssClass(cssPrefix, 'list-view-waiting-div', {
    background: 'rgba(255,255,255,.6)',
    textAlign: 'center',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 4 // The cardview thumbnail has a z-index of 3, so the value is set to 4 to make it appear above the grid view.
  });

  export const NoContentDiv = cssClass(cssPrefix, 'no-content-div', {
    fontSize: '18px',
    color: TabStyles!.Typography!.Color!.PrimaryLight,
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: 0,
    top: 0
  });

  export const NoContentImg = cssClass(cssPrefix, 'no-content-img', {
    height: 80,
    width: 80,
    display: 'block',
    margin: '0 auto'
  });

  export const DataGridBodyTopBorder = cssClass(cssPrefix, 'data-grid-body-top-border', {
    $nest: {
      '.tb-react-dg-body': {
        borderTop: `1px solid ${CommonStyles.Colors.DividerLineColor}`
      }
    }
  });

  export const ListView = cssClass(cssPrefix, 'list-view', fillStyle, {
  fontWeight: 'normal',
    // We want a small "margin" above the list view header. To do this, use the fillStyle
    // (absolute positioning) and a positive "top" value.
    // We can't use margin-top / height 100% because it'll move everything down and show
    // an extra scroll bar
    top: 12,
    fontSize: 14,
    color: TabStyles.Typography.Color.Primary,
    $nest: {
      [`.tb-react-dg-hcell`]: {
       fontSize: 12,
       color: TabStyles.Typography.Color.PrimaryLight
      },
      ['.tb-checkbox-cell']: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: LIST_VIEW_ROW_OUTER_HEIGHT,
        width: LIST_VIEW_ROW_OUTER_HEIGHT,
        paddingBottom: 0,
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0
      },
      ['.tb-data-grid-icon']: {
        height: LIST_VIEW_ROW_OUTER_HEIGHT + 'px !important' as any,
        marginTop: -2
      },
      ['.tb-favorite-button-cell']: {
        width: LIST_VIEW_ROW_OUTER_HEIGHT,
        height: LIST_VIEW_ROW_OUTER_HEIGHT,
        lineHeight: LIST_VIEW_ROW_OUTER_HEIGHT,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }
  });

  export const SpaceHeaderContent = cssClass(cssPrefix, 'space-header-content',  {
    marginTop: TabStyles.Spacing.MediumSpace,
    marginBottom: TabStyles.Spacing.MediumSpace,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    height: 18
  });

  export const SiteSpaceContentFilterButton = cssClass(cssPrefix, 'site-space-content-filter-button', {
    fontWeight: 'normal',
    fontSize: 14,
    color: TabStyles.Typography.Color.PrimaryMedium,
  });

  export const SpaceHeaderText = cssClass(cssPrefix, 'space-header-text', {
    fontWeight: 'normal',
    fontSize: TabStyles.Typography.Sizing.XXLarge,
    margin: 0,
    marginRight: TabStyles.Spacing.ExtraLargeSpace
  });

  export const SpaceSubHeaderText = cssClass(cssPrefix, 'space-sub-header-text', {
    fontWeight: 'bold',
    fontSize: TabStyles.Typography.Sizing.XLarge,
    margin: 0,
    marginRight: TabStyles.Spacing.ExtraLargeSpace
  });

  export const SpinnerContainer = cssClass(cssPrefix, 'spinner-container', {
    paddingTop: 48
  });
}
